<template>
  <div class="relative" v-closable="closeDropdown" @keydown="handleKeydown">
    <input
      :id="id"
      type="text"
      class="w-full border px-4 py-2 cursor-pointer"
      :value="selectedLabel"
      :disabled="disabled"
      @click="toggleDropdown"
      readonly
    />
    <span
      class="absolute right-2 top-1/2 transform -translate-y-1/2 cursor-pointer"
      @click="toggleDropdown"
    >
      <arrow-icon class="w-2" />
    </span>

    <div
      v-if="isOpen"
      :class="[
        'absolute w-full z-10 rounded-b bg-white shadow max-h-72 overflow-y-auto',
        dropdownPosition,
      ]"
    >
      <button
        v-for="(option, index) in options"
        :key="index"
        @click="selectOption(option)"
        :disabled="option.disabled"
        :class="[
          'text-left w-full block px-4 py-2 text-sm sm:text-base',
          { 'text-content-300': option.value === null || option.disabled },
          {
            'cursor-pointer hover:bg-primary-300 hover:bg-opacity-10 hover:text-primary-500 smooth':
              !option.disabled,
          },
          {
            'bg-primary-300 bg-opacity-10 text-primary-500 smooth':
              option.value === modelValue,
          },
        ]"
      >
        {{ option.label }}
      </button>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    id: String,
    disabled: Boolean,
    options: {
      type: Array,
      required: true,
    },
    modelValue: {
      type: [String, Number, Boolean],
      default: null,
    },
  },
  data() {
    return {
      isOpen: false,
      dropdownPosition: "top-full", // Default to show below input
      searchText: "",
    };
  },
  computed: {
    selectedLabel() {
      const selectedOption = this.options.find(
        (option) => option.value === this.modelValue
      );
      return selectedOption ? selectedOption.label : "";
    },
  },
  methods: {
    toggleDropdown() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.positionDropdown();
      }
    },
    selectOption(option) {
      this.$emit("update:modelValue", option.value);
      this.$emit("change");
      this.isOpen = false;
      this.searchText = ""; // Reset search text after selection
    },
    closeDropdown() {
      this.isOpen = false;
    },
    positionDropdown() {
      const inputRect = this.$el.getBoundingClientRect();
      const availableSpaceBelow = window.innerHeight - inputRect.bottom;
      const availableSpaceAbove = inputRect.top;

      if (
        availableSpaceBelow < 150 &&
        availableSpaceAbove > availableSpaceBelow
      ) {
        this.dropdownPosition = "bottom-full"; // Show dropdown above input
      } else {
        this.dropdownPosition = "top-full"; // Show dropdown below input
      }
    },
    handleKeydown(event) {
      if (!this.isOpen) this.toggleDropdown();

      if (event.key === "Enter" || event.key === "Tab") {
        // Close the dropdown when Enter is pressed
        this.isOpen = false;
        return;
      }

      // Add typed character to `searchText`
      if (event.key.length === 1 && !event.ctrlKey && !event.metaKey) {
        this.searchText += event.key.toLowerCase();

        // Find the first matching option
        const matchingOption = this.options.find((option) =>
          option.label.toLowerCase().startsWith(this.searchText)
        );
        if (matchingOption) {
          this.$emit("update:modelValue", matchingOption.value);
          this.scrollToOption(matchingOption);
        }
      }

      // Reset `searchText` after a short delay
      clearTimeout(this.clearSearchTextTimeout);
      this.clearSearchTextTimeout = setTimeout(() => {
        this.searchText = "";
      }, 500);
    },
    scrollToOption(option) {
      this.$nextTick(() => {
        const index = this.options.indexOf(option);
        const optionElement = this.$el.querySelectorAll("button")[index];
        if (optionElement) {
          optionElement.scrollIntoView({ block: "nearest" });
        }
      });
    },
  },
};
</script>
